import React, { useEffect, useRef, useState } from 'react';
import { Button, Drawer, message } from 'antd';
import { original, produce } from 'immer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ErrorUtils from 'src/utils/ErrorUtils';
import { isArray, isOneOf, nonNullish } from 'src/utils/typeGuards';
import { arrayWrap } from 'src/utils/arrayWrap';
import Spin from 'src/components/common/Spin';
import ConfirmationModal from 'src/components/common/ConfirmationModal';
import { DetailsValidation, BlockDetails } from './BlockDetails';
import { BlockContent } from './BlockContent';
import { createEditAlert, createEditAlertCriteria } from '../api/alertConfiguration.api';
import {
  AlertItem,
  Details,
  AnyCriteria,
  DeletedCriteria,
  CreateEditAlertDetails,
  isNewCriteria,
  isUpdatedCriteria,
  CriteriaValue,
  getCriteriaValueKeys,
} from './types/AlertConfiguration';
import { CriteriaError, DuplicateFoundError, ValidationError } from './BlockContent/AlertCriteria';
import { useAlertCriteriaGroups } from './hooks/useAlertCriteriaGroups';
import styles from './AlertConfiguration.module.less';

const configMessage = {
  create: {
    alert: 'The Alert was created successfully.',
    criteria: 'The AlertCriteria was added successfully.',
  },
  update: {
    alert: 'The Alert was updated successfully.',
    criteria: 'The AlertCriteria was updated successfully.',
  },
};

type AlertConfigurationProps = {
  alert?: AlertItem;
  segmentId?: number;
  isModalOpen: boolean;
  hideModal: () => void;
  onComplete: () => void;
};

const isCriteriaValueEqualTo =
  (a: Partial<CriteriaValue>) =>
  (b: Partial<CriteriaValue>): boolean => {
    return getCriteriaValueKeys().every((key) => String(a[key]) === String(b[key]));
  };

// TODO: after combining error with criteria this will not be required anymore
const getCriteriaIndexes = (criteriaGroups: AnyCriteria[][]): [number, number][] => {
  return criteriaGroups.flatMap((group, i) => group.map((_, j) => [i, j] as [number, number]));
};

export const AlertConfiguration: React.FC<AlertConfigurationProps> = ({
  alert,
  segmentId,
  isModalOpen,
  hideModal,
  onComplete,
}) => {
  const [details, setDetails] = useState<Details>({
    name: alert?.alertName ?? '',
    description: alert?.alertDescription ?? '',
    segmentIds: alert?.segmentId ?? arrayWrap(segmentId),
    subscriptions: alert?.emailSubscription?.split(/\s*,\s*/) ?? [],
  });
  const { loading, criteriaGroups: groups } = useAlertCriteriaGroups(alert?.alertId);
  const [criteriaGroups, setCriteriaGroups] = useState<AnyCriteria[][]>(groups ?? []);
  const [copiedCriteria, setCopiedCriteria] = useState<AnyCriteria | null>(null);
  const [copiedError, setCopiedError] = useState<CriteriaError | null>(null);
  const [criteriaErrors, setCriteriaErrors] = useState<(CriteriaError | null)[][]>([]);
  const deletedCriteria = useRef<DeletedCriteria[]>([]);
  const changedCriteriaList = criteriaGroups.flat().filter((criteria) => criteria.operation !== null);
  const [isConfirmationCloseVisible, setIsConfirmationCloseVisible] = useState<boolean>(false);

  useEffect(() => {
    if (alert?.alertId && groups?.length) {
      setCriteriaGroups(groups);
      // TODO: we must combine error with criteria into a wrapper
      setCriteriaErrors(groups.map((els) => Array(els.length).fill(null)));
    }
  }, [alert?.alertId, groups]);

  const isSaveDisabled = (details: DetailsValidation): boolean => {
    if (details.hasErrors || !criteriaGroups.length || criteriaErrors.some((errors) => errors.some(nonNullish))) {
      return true;
    }
    return !details.hasChanges && !changedCriteriaList.length && !deletedCriteria.current.length;
  };

  const getDuplicatesIndexes = (duplicates: AnyCriteria[]): [number, number][] => {
    return getCriteriaIndexes(criteriaGroups).filter(([i, j]) => duplicates.includes(criteriaGroups[i][j]));
  };

  const validatePreviousDuplicates = (draft: (CriteriaError | null)[][], prevCriteria: AnyCriteria) => {
    const criteriaList = criteriaGroups.flat().filter((item) => item !== prevCriteria);
    const prevDuplicates = criteriaList.filter(isCriteriaValueEqualTo(prevCriteria));
    const isDuplicateFoundError = ([i, j]: [number, number]) => draft[i][j] instanceof DuplicateFoundError;

    if (prevDuplicates.length > 0) {
      const prevDuplicatesIndexes = getDuplicatesIndexes(prevDuplicates);
      if (prevDuplicatesIndexes.length === 1) {
        const [i, j] = prevDuplicatesIndexes[0];
        if (draft[i][j] instanceof DuplicateFoundError) {
          draft[i][j] = null;
        }
      } else {
        if (!prevDuplicatesIndexes.some(isDuplicateFoundError)) {
          const [i, j] = prevDuplicatesIndexes[0];
          draft[i][j] = new DuplicateFoundError();
        }
      }
    }
  };

  const saveAlert = async (savingGroups: AnyCriteria[][], deletedCriteria: DeletedCriteria[]) => {
    const alertDetails: CreateEditAlertDetails.Request = {
      alertName: details.name,
      alertDescription: details.description,
      emailSubscription: details.subscriptions.join(','),
      segmentId: details.segmentIds.map(Number).filter(Boolean),
    };
    const successMessage = alert?.alertId ? configMessage.update : configMessage.create;

    if (alert?.alertId) {
      alertDetails.alertId = alert.alertId;
    }

    try {
      const resAlert = await createEditAlert(alertDetails);
      message.success(successMessage.alert);

      // FIXME: `Criteria` type is wrong for `competitorOwnerId` field since it's an array of strings instead of a number
      const transformCriteria = <T extends AnyCriteria | DeletedCriteria>(criteria: T): T => {
        // TODO: fix typings because currently Object.fromEntries() returns `any`
        const mapEntries = ([k, v]: [string, unknown]) => [k, isArray(v) ? v.join() : v];
        return Object.fromEntries(Object.entries(criteria).map(mapEntries));
      };

      const changedCriteria = savingGroups.flatMap((group, i) =>
        group
          .filter(isOneOf(isNewCriteria, isUpdatedCriteria))
          .map((criteria) => transformCriteria(criteria) as Required<typeof criteria>)
          .map((criteria) => ({
            ...criteria,
            alertId: alert?.alertId ?? resAlert.responseHeader.id,
            alertGroupId: i + 1,
            // TODO: `alertExpression` should be removed when BE is fixed
            alertExpression: 'FAKE-EXPRESSION',
          }))
      );

      const requestBody = [
        ...changedCriteria, //
        ...(deletedCriteria.map(transformCriteria) as Required<DeletedCriteria>[]),
      ];

      if (requestBody.length > 0) {
        await createEditAlertCriteria(requestBody);
        message.success(successMessage.criteria);
      }

      onComplete();
    } catch (e) {
      message.error(ErrorUtils.getMessage(e));
      console.log(e);
    }
  };

  const addAnd = () => {
    setCriteriaGroups(
      produce((draft: AnyCriteria[][]) => {
        draft.push([{ operation: 'add' }]);
      })
    );
    // TODO: we must combine error with criteria into a wrapper
    setCriteriaErrors(
      produce((draft: (CriteriaError | null)[][]) => {
        draft.push([new ValidationError({})]);
      })
    );
  };

  const handleAddOr = (indexAnd: number) => {
    setCriteriaGroups(
      produce((draft: AnyCriteria[][]) => {
        draft[indexAnd].push({ operation: 'add' });
      })
    );
    // TODO: we must combine error with criteria into a wrapper
    setCriteriaErrors(
      produce((draft: (CriteriaError | null)[][]) => {
        draft[indexAnd].push(new ValidationError({}));
      })
    );
  };
  const onCriteriaPaste = (indexAnd: number, indexOr: number) => {
    if (copiedCriteria) {
      setCriteriaGroups(
        produce((draft: AnyCriteria[][]) => {
          if (isNewCriteria(copiedCriteria)) {
            draft[indexAnd].splice(indexOr + 1, 0, { ...copiedCriteria });
          } else {
            const { alertCriteriaId, ...newCriteria } = copiedCriteria;
            draft[indexAnd].splice(indexOr + 1, 0, { ...newCriteria, operation: 'add' });
          }
        })
      );
      setCriteriaErrors(
        produce((draft: (CriteriaError | null)[][]) => {
          draft[indexAnd].splice(
            indexOr + 1,
            0,
            copiedError instanceof ValidationError ? copiedError : new DuplicateFoundError()
          );
        })
      );
      message.info('Pasted');
    }
  };
  const onCriteriaCopy = (criteria: AnyCriteria) => {
    setCopiedCriteria(criteria);
    message.info('Copied');
  };

  const handleRemoveOr = (iAnd: number, iOr: number) => {
    setCriteriaGroups(
      produce((draft: AnyCriteria[][]) => {
        const [criteria] = draft[iAnd].splice(iOr, 1);
        if (!isNewCriteria(criteria)) {
          deletedCriteria.current.push({ ...original(criteria)!, operation: 'del' });
        }
        if (draft[iAnd].length === 0) {
          draft.splice(iAnd, 1);
        }
      })
    );
    // TODO: we must combine error with criteria into a wrapper
    setCriteriaErrors(
      produce((draft: (CriteriaError | null)[][]) => {
        validatePreviousDuplicates(draft, criteriaGroups[iAnd][iOr]);

        draft[iAnd].splice(iOr, 1);
        if (draft[iAnd].length === 0) {
          draft.splice(iAnd, 1);
        }
      })
    );
  };

  const handleCriteriaChange = (iAnd: number, iOr: number, criteria: AnyCriteria, error: ValidationError | null) => {
    setCriteriaGroups(
      produce((draft: AnyCriteria[][]) => {
        if (original(draft[iAnd][iOr]) !== criteria) {
          draft[iAnd][iOr] = isNewCriteria(criteria) ? criteria : { ...criteria, operation: 'update' };
        }
      })
    );
    // TODO: we must combine error with criteria into a wrapper
    setCriteriaErrors(
      produce((draft: (CriteriaError | null)[][]) => {
        const prevCriteria = criteriaGroups[iAnd][iOr];
        const criteriaList = criteriaGroups.flat().filter((item) => item !== prevCriteria);
        const currentDuplicates = criteriaList.filter(isCriteriaValueEqualTo(criteria));

        validatePreviousDuplicates(draft, prevCriteria);

        if (error) {
          draft[iAnd][iOr] = error;
        } else if (currentDuplicates.length > 0) {
          getDuplicatesIndexes(currentDuplicates)
            .filter(([i, j]) => draft[i][j] instanceof DuplicateFoundError)
            .forEach(([i, j]) => (draft[i][j] = null));
          draft[iAnd][iOr] = new DuplicateFoundError();
        } else {
          draft[iAnd][iOr] = null;
        }
      })
    );
  };

  const handleClose = () => {
    if (changedCriteriaList.length || deletedCriteria.current.length) {
      return setIsConfirmationCloseVisible(true);
    }
    hideModal();
  };

  return (
    <>
      <Drawer
        visible={isModalOpen}
        onClose={handleClose}
        placement="bottom"
        closable={false}
        height={'100%'}
        zIndex={999}
      >
        <div className={styles.xSpaceBetween}>
          <div />
          <h1 className={styles.title}>{alert ? 'Edit Alert' : 'Create Alert'}</h1>
          <Button onClick={handleClose} type="link">
            <FontAwesomeIcon icon={['fal', 'times']} size={'3x'} />
          </Button>
        </div>
        <div className={styles.root}>
          <div className={styles.leftBlock}>
            <BlockDetails
              details={details}
              isSaveDisabled={isSaveDisabled}
              setDetails={setDetails}
              onSave={() => saveAlert(criteriaGroups, deletedCriteria.current)}
            />
          </div>
          <div className={styles.rightBlock}>
            {alert ? (
              <Spin spinning={loading}>
                {!!criteriaGroups && (
                  <BlockContent
                    criteriaGroups={criteriaGroups}
                    criteriaErrors={criteriaErrors}
                    handleAddOr={handleAddOr}
                    handleRemoveOr={handleRemoveOr}
                    handleCriteriaChange={handleCriteriaChange}
                    addAnd={addAnd}
                    onCriteriaPaste={onCriteriaPaste}
                    onCriteriaCopy={onCriteriaCopy}
                    isCopiedCriteria={!!copiedCriteria}
                    setCopiedError={setCopiedError}
                  />
                )}
              </Spin>
            ) : (
              <BlockContent
                criteriaGroups={criteriaGroups}
                criteriaErrors={criteriaErrors}
                handleAddOr={handleAddOr}
                handleRemoveOr={handleRemoveOr}
                handleCriteriaChange={handleCriteriaChange}
                addAnd={addAnd}
                onCriteriaPaste={onCriteriaPaste}
                onCriteriaCopy={onCriteriaCopy}
                isCopiedCriteria={!!copiedCriteria}
                setCopiedError={setCopiedError}
              />
            )}
          </div>
        </div>
      </Drawer>

      <ConfirmationModal
        visible={isConfirmationCloseVisible}
        confirmText="Yes"
        onClose={() => setIsConfirmationCloseVisible(false)}
        onConfirm={hideModal}
      >
        <h2>
          Are you sure you want to close form? <br />
          Your unsaved data will be lost.
        </h2>
      </ConfirmationModal>
    </>
  );
};
